/* eslint-disable react/prop-types */
import React, { useState, useMemo } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import { useTheme } from 'styled-components';
import ButtonV2 from 'components/ButtonV2';
import {
  PageShell,
  PageHeader,
  DataTable,
  HeaderLabel,
  Icon,
  StatStrip,
  StatTile,
  StatusPill,
  ActionIcon,
  SegmentedFilter,
  Money,
  Num,
  Split,
  Strong,
  useIsMobile,
  tokens,
} from 'components/v3';
import { csvSlug, downloadCsv } from 'shared/csv';
import { formatCurrency } from 'shared/formatCurrency';
import { HELP } from 'shared/profitHelp';
import {
  ledgerPeriodOptions,
  periodYear,
  YEAR_TO_DATE,
} from 'shared/reportPeriods';
import { useGetAccountReporting } from '../gql/queries/useGetAccountReporting';
import { useGetAnnualPerformance } from '../gql/queries/useGetAnnualPerformance';
import { useGetEventPerformance } from '../gql/queries/useGetEventPerformance';
import { useGetRefundsByAccount } from '../gql/queries/useGetRefundsByAccount';

// Promoter Profitability Dashboard — gen-3 redesign. Header with the account
// lockup and a year filter, a KPI tile strip, the Event Performance, Recent
// Refunds and Membership & Annual Sales tables as v3 DataTables, and an
// app-vs-POS summary card. Composed from v3 components and tokens.

// One label/value line in a summary card, with an optional leading pill.
const SummaryRow = ({ pill, label, value, last }) => {
  const t = tokens(useTheme());
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 14,
        padding: '13px 0',
        borderBottom: last ? 'none' : '1px solid ' + t.line,
      }}
    >
      <span
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 12,
          minWidth: 0,
        }}
      >
        {pill}
        <span style={{ fontSize: 13.5, fontWeight: 600, color: t.sub }}>
          {label}
        </span>
      </span>
      <span
        style={{
          fontSize: 15.5,
          fontWeight: 700,
          color: t.primary,
          fontVariantNumeric: 'tabular-nums',
        }}
      >
        {value}
      </span>
    </div>
  );
};

const SummaryCard = ({ title, children }) => {
  const t = tokens(useTheme());
  return (
    <div
      style={{
        background: t.card,
        border: '1px solid ' + t.border,
        borderRadius: 16,
        boxShadow: t.shadowCard,
        padding: '20px 22px',
      }}
    >
      <HeaderLabel>{title}</HeaderLabel>
      <div style={{ marginTop: 6 }}>{children}</div>
    </div>
  );
};

const PILL = {
  app: {
    color: '#2C6FD6',
    background: 'rgba(44,111,214,0.13)',
    border: 'rgba(44,111,214,0.34)',
  },
  pos: {
    color: '#64748b',
    background: 'rgba(100,116,139,0.13)',
    border: 'rgba(100,116,139,0.34)',
  },
  ok: {
    color: '#1F9D57',
    background: 'rgba(52,210,123,0.13)',
    border: 'rgba(52,210,123,0.34)',
  },
  warn: {
    color: '#B7791F',
    background: 'rgba(214,158,46,0.14)',
    border: 'rgba(214,158,46,0.36)',
  },
  error: {
    color: '#D63A24',
    background: 'rgba(214,58,36,0.12)',
    border: 'rgba(214,58,36,0.34)',
  },
};

const refundStatusPill = (status) => {
  const style =
    status === 'Completed'
      ? PILL.ok
      : status === 'Processing'
      ? PILL.warn
      : status === 'Failed'
      ? PILL.error
      : PILL.pos;
  return <StatusPill label={status} {...style} />;
};

const EVENT_COLUMNS = [
  { key: 'date', label: 'Date', width: '120px' },
  {
    key: 'event_name',
    label: 'Event Name',
    width: 'minmax(220px,2fr)',
    primary: true,
  },
  { key: 'sales', label: 'Sales', width: '110px', align: 'right' },
  { key: 'app_pos', label: 'App / POS', width: '110px', align: 'right' },
  { key: 'cash_card', label: 'Cash / Card', width: '110px', align: 'right' },
  {
    key: 'app_profit',
    label: 'App Profit',
    help: HELP.appProfit,
    width: '110px',
    align: 'right',
  },
  {
    key: 'pos_profit',
    label: 'POS Profit',
    help: HELP.posProfit,
    width: '110px',
    align: 'right',
  },
  {
    key: 'total_profit',
    label: 'Total Profit',
    width: '110px',
    align: 'right',
  },
  {
    key: 'actions',
    label: 'Actions',
    width: '70px',
    align: 'right',
    skeleton: 'actions',
    actionsCount: 1,
  },
];

const EVENT_CSV = [
  { header: 'Date', value: (e) => e.date },
  { header: 'Event', value: (e) => e.event_name },
  { header: 'App Sales', value: (e) => e.app_sales.toFixed(2) },
  { header: 'POS Card', value: (e) => e.pos_card_sales.toFixed(2) },
  { header: 'POS Cash', value: (e) => e.pos_cash_sales.toFixed(2) },
  { header: 'App Profit', value: (e) => e.app_profit.toFixed(2) },
  { header: 'POS Profit', value: (e) => e.pos_profit.toFixed(2) },
  { header: 'Total Profit', value: (e) => e.total_profit.toFixed(2) },
];

const REFUND_COLUMNS = [
  {
    key: 'purchaserOrderNumber',
    label: 'Order #',
    width: '100px',
    primary: true,
  },
  { key: 'customerName', label: 'Customer', width: 'minmax(140px,1fr)' },
  { key: 'eventName', label: 'Event', width: 'minmax(180px,1.5fr)' },
  { key: 'refundTotal', label: 'Refund Total', width: '110px', align: 'right' },
  { key: 'trackAmount', label: 'Track Amount', width: '110px', align: 'right' },
  { key: 'feeImpact', label: 'Fee Impact', width: '100px', align: 'right' },
  { key: 'refundDate', label: 'Date', width: '110px' },
  { key: 'reason', label: 'Reason', width: '130px' },
  { key: 'status', label: 'Status', width: '120px', skeleton: 'pill' },
  {
    key: 'actions',
    label: 'Actions',
    width: '70px',
    align: 'right',
    skeleton: 'actions',
    actionsCount: 1,
  },
];

// Total sales plus the two shares, from a row's app / POS card / POS cash
// sales. Shared by the event and annual tables.
const salesCells = (row) => ({
  sales: (
    <Money value={row.app_sales + row.pos_card_sales + row.pos_cash_sales} />
  ),
  app_pos: (
    <Split a={row.app_sales} b={row.pos_card_sales + row.pos_cash_sales} />
  ),
  cash_card: <Split a={row.pos_cash_sales} b={row.pos_card_sales} />,
});

const ANNUAL_TYPE_LABEL = { annual: 'Season Pass', membership: 'Membership' };

const ANNUAL_COLUMNS = [
  { key: 'name', label: 'Pass', width: 'minmax(220px,2fr)', primary: true },
  { key: 'type', label: 'Type', width: '130px', skeleton: 'pill' },
  { key: 'passes_sold', label: 'Passes Sold', width: '110px', align: 'right' },
  { key: 'sales', label: 'Sales', width: '110px', align: 'right' },
  { key: 'app_pos', label: 'App / POS', width: '110px', align: 'right' },
  { key: 'cash_card', label: 'Cash / Card', width: '110px', align: 'right' },
  {
    key: 'profit',
    label: 'Our Profit',
    help: HELP.passProfit,
    width: '110px',
    align: 'right',
  },
  {
    key: 'avg_profit',
    label: 'Avg Profit/Pass',
    width: '130px',
    align: 'right',
  },
];

function PromoterDashboardContent() {
  const isMobile = useIsMobile();
  const history = useHistory();
  const params = useParams();
  const [period, setPeriod] = useState(YEAR_TO_DATE);

  const currentYear = new Date().getFullYear();
  const periodOptions = useMemo(
    () => ledgerPeriodOptions(currentYear),
    [currentYear]
  );

  const accountId = parseInt(params.id, 10);
  const year = periodYear(period, currentYear);

  const {
    loading: accountLoading,
    account,
    metrics,
  } = useGetAccountReporting(accountId, year);
  const { data: refundsData, loading: refundsLoading } = useGetRefundsByAccount(
    { accountId }
  );
  const { data: eventPerformanceData, loading: eventPerformanceLoading } =
    useGetEventPerformance(accountId, year);
  const { data: annualPerformanceData, loading: annualPerformanceLoading } =
    useGetAnnualPerformance(accountId, year);

  const accountName = account ? account.business_dba : '';
  const totalProfit = metrics ? metrics.total_platform_profit : 0;
  const appProfit = metrics ? metrics.app_profit : 0;
  const posProfit = metrics ? metrics.pos_profit : 0;
  const eventCount = metrics ? metrics.events_count : 0;
  // Both balances arrive pre-formatted from the API, or null.
  const stripeBalance = account?.stripe_balance || formatCurrency(0);
  const payoutBalance = account?.payout_balances?.total || formatCurrency(0);

  const events = eventPerformanceData?.getEventPerformanceV2 || [];
  const annuals = annualPerformanceData?.getAnnualPerformanceV2 || [];

  const recentRefunds =
    refundsData?.getRefundsByAccount?.results?.map((refund) => ({
      id: refund.id,
      purchaserOrderNumber: refund.purchase.id,
      customerName: refund.user ? refund.user.formatted_name : '',
      eventName: refund?.purchase?.event?.name,
      refundTotal: refund.total,
      trackAmount: refund.track_amount,
      feeImpact: -Math.abs(refund.fees),
      refundDate: new Date(refund.date).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      }),
      reason: 'Customer Request',
      status: 'Completed',
    })) || [];

  const share = (part) =>
    totalProfit > 0 ? `${((100 * part) / totalProfit).toFixed(1)}%` : '—';

  const renderEventRow = (row) => ({
    date: row.date,
    event_name: <Strong>{row.event_name}</Strong>,
    ...salesCells(row),
    app_profit: <Money value={row.app_profit} strong />,
    pos_profit: <Money value={row.pos_profit} strong />,
    total_profit: <Money value={row.total_profit} strong />,
    actions: (
      <ActionIcon
        icon="view"
        title="View event"
        onClick={() => history.push(`/admin/events/menu/${row.event_id}`)}
      />
    ),
  });

  const renderRefundRow = (row) => ({
    purchaserOrderNumber: <Strong>{row.purchaserOrderNumber}</Strong>,
    customerName: row.customerName,
    eventName: row.eventName || '',
    refundTotal: <Money value={row.refundTotal} />,
    trackAmount: <Money value={row.trackAmount} />,
    feeImpact: <Money value={row.feeImpact} strong negative />,
    refundDate: row.refundDate,
    reason: row.reason,
    status: refundStatusPill(row.status),
    actions: (
      <ActionIcon
        icon="view"
        title="View transaction"
        onClick={() =>
          history.push(`/admin/transactions/detail/${row.purchaserOrderNumber}`)
        }
      />
    ),
  });

  const renderAnnualRow = (row) => ({
    name: <Strong>{row.name}</Strong>,
    type: (
      <StatusPill
        label={ANNUAL_TYPE_LABEL[row.type] || row.type}
        dot={false}
        {...(row.type === 'membership' ? PILL.app : PILL.pos)}
      />
    ),
    passes_sold: <Num value={row.passes_sold} />,
    ...salesCells(row),
    profit: <Money value={row.profit} strong />,
    avg_profit: <Money value={row.avg_profit} strong />,
  });

  const section = { marginBottom: isMobile ? 16 : 24 };

  return (
    <PageShell>
      <PageHeader
        icon="circle-dollar-sign"
        title="Promoter Profitability"
        subtitle={accountName || (accountLoading ? 'Loading…' : '')}
        logo={account?.logo || undefined}
      >
        <SegmentedFilter
          value={period}
          onChange={setPeriod}
          options={periodOptions}
        />
      </PageHeader>

      {/* KPI strip */}
      <StatStrip style={section}>
        <StatTile
          label="Total Platform Profit"
          value={formatCurrency(totalProfit)}
          loading={accountLoading}
          accent
        />
        <StatTile
          label="App Profit"
          value={formatCurrency(appProfit)}
          loading={accountLoading}
        />
        <StatTile
          label="POS Profit"
          value={formatCurrency(posProfit)}
          loading={accountLoading}
        />
        <StatTile
          label="Events This Period"
          value={eventCount}
          loading={accountLoading}
        />
        <StatTile
          label="Stripe Balance"
          value={stripeBalance}
          loading={accountLoading}
        />
        <StatTile
          label="Payout Balance"
          value={payoutBalance}
          loading={accountLoading}
        />
      </StatStrip>

      <div style={section}>
        <DataTable
          columns={EVENT_COLUMNS}
          data={events}
          renderRow={renderEventRow}
          keyField="event_id"
          loading={eventPerformanceLoading}
          emptyLabel="No events found for this period."
          title="Event Performance"
          help={HELP.eventPerformance}
          action={
            <ButtonV2
              icon={<Icon name="download" size={16} />}
              disabled={eventPerformanceLoading || !events.length}
              onClick={() =>
                downloadCsv(
                  `${csvSlug(accountName)}-event-performance-${year}.csv`,
                  EVENT_CSV,
                  events
                )
              }
              style={{ width: 'auto' }}
            >
              Export to CSV
            </ButtonV2>
          }
        />
      </div>

      <div style={section}>
        <DataTable
          columns={REFUND_COLUMNS}
          data={recentRefunds}
          renderRow={renderRefundRow}
          loading={refundsLoading}
          emptyLabel="No refunds found for this account."
          title="Recent Refunds"
          action={
            <ButtonV2
              onClick={() =>
                history.push(`/admin/accounts/refunds/${params.id}`)
              }
              style={{ width: 'auto' }}
            >
              View All Refunds
            </ButtonV2>
          }
        />
      </div>

      <div style={section}>
        <DataTable
          columns={ANNUAL_COLUMNS}
          data={annuals}
          renderRow={renderAnnualRow}
          keyField="ticket_id"
          loading={annualPerformanceLoading}
          emptyLabel="No season passes or memberships sold in this period."
          title="Membership & Annual Sales"
          help={HELP.annualSales}
        />
      </div>

      {/* Summary card */}
      <div style={{ maxWidth: 560 }}>
        <SummaryCard title="App vs POS Performance">
          <SummaryRow
            pill={<StatusPill label="App" dot={false} {...PILL.app} />}
            label={`${share(appProfit)} of total profit`}
            value={formatCurrency(appProfit)}
          />
          <SummaryRow
            pill={<StatusPill label="POS" dot={false} {...PILL.pos} />}
            label={`${share(posProfit)} of total profit`}
            value={formatCurrency(posProfit)}
            last
          />
        </SummaryCard>
      </div>
    </PageShell>
  );
}

export default function PromoterDashboard() {
  return <PromoterDashboardContent />;
}
